import { Form } from "antd";
import { useEffect, useState } from "react";
import DynamicForm from "../controls/DynamicForm";
import { TemplateEventsForm } from "./TemplatesEventsFormDialog.default";
import {
  addTemplateEvent,
  deleteTemplateEvent,
  updateTemplateEvent,
  getTemplates,
} from "../repository/data.repository";
import { Template } from "../repository/data.default";
import { nameOf } from "../utils/typescript";
import { QueryOperation } from "../code/search-query/search-query";
import { retry } from "../utils/http";
import { EventTriggerType } from "../repository/data.default";
import InModal from "../controls/InModal";
import { modalTitle } from "../controls/InModal.default";
import ModalFooter from "../controls/ModalFooter";
import ModalDeleteConfirmation from "../controls/ModalDeleteConfirmation";
import ModalErrorAlert from "../controls/ModalErrorAlert";
import { setFormFields } from "../utils/setFormFields";
import { hasFormTouched } from "../utils/hasFormChanged";
import { getProblemMessage } from "../utils/http";
import { enumRequest } from "../utils/options";
export interface TemplatesEventsFormDialogProps {
  readonly open?: boolean;
  readonly mode: "add" | "update" | "delete";
  readonly initialValues?: TemplateEventsForm;
  readonly onFinish?: () => void;
  readonly onCancel?: () => void;
}

function TemplatesEventsFormDialog(props: TemplatesEventsFormDialogProps) {
  const [form] = Form.useForm();
  const values = Form.useWatch<TemplateEventsForm>([], form);

  const [valid, setValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  function onError(error: unknown): void {
    setLoading(false);
    setErrorMessage(getProblemMessage(error, "Something went wrong, try again"));
  }

  useEffect(() => {
    if (props.open) {
      if (props.initialValues) setFormFields(form, props.initialValues);
      else form.setFieldsValue(props.initialValues);
    } else {
      reset();
    }
  }, [props.open]);

  useEffect(() => {
    form
      .validateFields({
        validateOnly: true,
      })
      .then((value) => {
        setValid(!!Object.values(value).length);
      })
      .catch(() => {
        setValid(false);
      });
  }, [form, values, props.open]);

  function onSubmit(): void {
    const { event, isActive, template } = values;
    setLoading(true);
    setErrorMessage(null);
    if (props.mode === "add") {
      addTemplateEvent({
        uid: props.initialValues?.uid,
        event: event.value,
        isActive,
        templateUid: template.value,
      })
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch(onError);
    } else if (props.mode === "update") {
      if (hasFormTouched(form)) {
        updateTemplateEvent({
          uid: props.initialValues?.uid,
          rowVersion: props.initialValues?.rowVersion,
          templateUid: template.value,
          isActive,
          event: event.value,
        })
          .then(() => {
            setLoading(false);
            props.onFinish?.();
          })
          .catch(onError);
      } else {
        setLoading(false);
        props.onFinish?.();
      }
    } else if (props.mode === "delete") {
      deleteTemplateEvent(props.initialValues?.uid!)
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch(onError);
    }
  }

  function onCancel(): void {
    props.onCancel?.();
  }

  // Methods

  function reset(): void {
    setLoading(false);
    setValid(false);
    setErrorMessage(null);
    form.resetFields();
  }

  const templatesRequest = (search: string) => {
    return retry(() =>
      getTemplates({
        searchQuery: {
          conditions: [
            {
              field: nameOf<Template>("name"),
              operation: QueryOperation.StartsWith,
              values: [search],
            },
          ],
        },
        pageNumber: 0,
        pageSize: 100,
      }),
    ).then(({ data }) =>
      data.map((dataItem) => ({
        display: dataItem.name,
        value: dataItem.uid,
        dataItem,
      })),
    );
  };

  return (
    <InModal
      open={props.open}
      size={props.mode === "delete" ? "small" : "medium"}
      title={modalTitle(props.mode, "Trigger", props.initialValues?.event?.display)}
      onCancel={onCancel}
      actions={
        <ModalFooter
          mode={props.mode}
          loading={loading}
          valid={valid}
          onCancel={onCancel}
          onSubmit={onSubmit}
        />
      }
    >
      {props.mode !== "delete" && (
        <DynamicForm
          className="w-auto"
          form={form}
          schema={{
            groups: [
              {
                controls: [
                  {
                    type: "combobox",
                    name: "event",
                    label: "Event",
                    request: enumRequest(EventTriggerType),
                    rules: [
                      {
                        required: true,
                        message: "Event is required",
                      },
                    ],
                  },
                  {
                    type: "combobox",
                    name: "template",
                    label: "Template Name",
                    request: templatesRequest,
                    rules: [
                      {
                        required: true,
                        message: "Template Name is required",
                      },
                    ],
                  },
                  {
                    type: "togglebox",
                    name: "isActive",
                    label: "Active",
                    rules: [
                      {
                        required: false,
                        message: "Active is required",
                      },
                    ],
                  },
                ],
              },
            ],
          }}
        />
      )}
      {props.mode === "delete" && (
        <ModalDeleteConfirmation
          entityLabel="trigger"
          entityName={props.initialValues?.event?.display}
        />
      )}
      <ModalErrorAlert message={errorMessage} />
    </InModal>
  );
}

export default TemplatesEventsFormDialog;
